import { useMemo, useState, type JSX } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import { INVITE_BATCH_MAX, type JoiningMode, parseInviteEmails } from '@profiler/shared'

/**
 * Invite people: paste addresses, see which of them Quarry can invite, send.
 *
 * The preview comes from the same parser the API checks against, so an address
 * shown as good here is not refused there. Nothing goes out while any address
 * is bad. Inviting the good half of a paste would leave somebody believing the
 * typo went too.
 *
 * By default Quarry emails each new invitee the sign-in link, from
 * no-reply@habilelabs.io with replies going to whoever invited them. Untick it
 * and the dialog says Quarry sends nothing, and keeps the link one click from
 * the clipboard, because "I invited you" with no link is how an invitation
 * goes unused.
 */
export function InviteDialog({
  open,
  joining,
  busy,
  error,
  onClose,
  onInvite,
}: Readonly<{
  open: boolean
  joining: JoiningMode
  busy: boolean
  error: unknown
  onClose: () => void
  onInvite: (emails: string[], sendEmail: boolean) => void
}>): JSX.Element {
  const [text, setText] = useState('')
  const [copied, setCopied] = useState(false)
  const [sendEmail, setSendEmail] = useState(true)
  const parsed = useMemo(() => parseInviteEmails(text), [text])
  const count = parsed.valid.length
  const tooMany = count > INVITE_BATCH_MAX
  const canSend = count > 0 && parsed.invalid.length === 0 && !tooMany && !busy
  const signInLink = window.location.origin

  const copy = (): void => {
    void navigator.clipboard?.writeText(signInLink).then(
      () => setCopied(true),
      () => setCopied(false),
    )
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onExited: () => {
            setText('')
            setCopied(false)
            setSendEmail(true)
          },
        },
      }}
    >
      <DialogTitle>Invite people</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Paste habilelabs.io addresses, one per line or separated by commas. Use the address Outlook shows
          for them: an invitation is matched to the address they sign in with.
        </DialogContentText>
        <TextField
          label="Addresses"
          placeholder="ada@habilelabs.io, grace@habilelabs.io"
          multiline
          minRows={3}
          fullWidth
          autoFocus
          value={text}
          onChange={(event) => setText(event.target.value)}
          sx={{ mt: 2 }}
        />
        {count > 0 ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }} aria-label="Addresses to invite">
            {parsed.valid.map((email) => (
              <Chip key={email} size="small" label={email} />
            ))}
          </Stack>
        ) : null}
        {parsed.invalid.length > 0 ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Only habilelabs.io addresses can be invited. Fix or remove: {parsed.invalid.join(', ')}
          </Alert>
        ) : null}
        {tooMany ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            At most {INVITE_BATCH_MAX} addresses at a time.
          </Alert>
        ) : null}
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={<Checkbox checked={sendEmail} onChange={(event) => setSendEmail(event.target.checked)} />}
          label="Email each of them the sign-in link"
        />
        <Alert
          severity="info"
          sx={{ mt: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={copy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          }
        >
          {sendEmail
            ? 'Quarry emails each new invitation from no-reply@habilelabs.io. Replies come to you. The sign-in link, if you want to send it yourself too:'
            : 'Quarry will not email anybody. Tell them yourself, and send them the sign-in link:'}
          <Box component="span" sx={{ display: 'block', fontWeight: 600, my: 0.5 }}>
            {signInLink}
          </Box>
          {joining === 'entra'
            ? 'Until invitations are switched on, also assign them to Quarry in Microsoft Entra, or Microsoft will refuse their sign-in.'
            : null}
        </Alert>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error instanceof Error ? error.message : String(error)}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSend} onClick={() => onInvite(parsed.valid, sendEmail)}>
          {count === 1 ? 'Invite 1 person' : `Invite ${count} people`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
